import {
  Atom,
  Beaker,
  BookOpen,
  Calculator,
  Compass,
  FileText,
  FlaskConical,
  GraduationCap,
  Layers,
  Scale,
  NotebookPen,
  Table,
  Zap,
  Hexagon,
  Swords,
  Gamepad2,
  Timer,
  Flame,
  ShieldAlert,
  Beaker as BeakerIcon,
  Droplet,
  Search,
  Link2,
  Boxes,
  Grid3x3,
  Ruler,
  Battery,
  Gauge,
  Snowflake,
  Waves,
  Orbit,
  type LucideIcon,
} from "@/lib/icons";

/**
 * Information architecture for ElementPad.
 *
 * Tools are grouped by what the student is *doing*, not by chemistry topic,
 * so a beginner can find a calculator without knowing it's "stoichiometry".
 * This file is the single source of truth for the header, footer, and
 * the /explore overview page - keep them in sync by editing this list only.
 */

export type NavCategoryKey = "explore" | "calculate" | "practice" | "learn" | "play" | "lab" | "teach";

export interface NavTool {
  label: string;
  /** Short blurb shown on the /explore page and homepage cards. */
  description: string;
  path: string;
  icon: LucideIcon;
  /** Comma-separated keywords to help the /explore page search. */
  keywords?: string;
}

export interface NavCategory {
  key: NavCategoryKey;
  label: string;
  /** One-line summary of what tools in this group help you do. */
  tagline: string;
  icon: LucideIcon;
  tools: NavTool[];
}

export const NAV_CATEGORIES: NavCategory[] = [
  {
    key: "explore",
    label: "Explore",
    tagline: "Discover elements, atoms, and structure.",
    icon: Compass,
    tools: [
      {
        label: "Periodic Table",
        description: "Browse all 118 elements with properties, trends, and detail panels.",
        path: "/",
        icon: Table,
        keywords: "elements, periodic, group, period, block",
      },
      {
        label: "3D Atom Viewer",
        description: "Inspect Bohr-style atoms and electron shells in 3D.",
        path: "/atom",
        icon: Atom,
        keywords: "bohr, electron, shell, orbital, model",
      },
      {
        label: "Ion Engine",
        description: "Build ionic compounds and see charges balance in real time.",
        path: "/ions",
        icon: Zap,
        keywords: "ionic, cation, anion, charge, formula",
      },
      {
        label: "Organic Chemistry",
        description: "Explore molecules with ball-and-stick models and isomers.",
        path: "/organic",
        icon: Layers,
        keywords: "organic, molecule, isomer, ball stick, alkane",
      },
      {
        label: "Molecule Viewer",
        description: "Browse essential molecules in 2D skeletal and 3D ball-and-stick views.",
        path: "/tools/molecule-viewer",
        icon: Hexagon,
        keywords: "molecule, viewer, 3d, 2d, skeletal, benzene, methane, water, ethanol, structure",
      },
      {
        label: "Crystal Lattices",
        description: "3D explorer for ionic, covalent, metallic, and molecular solids — see why structure dictates property.",
        path: "/learn/crystal-lattices",
        icon: Boxes,
        keywords: "crystal, lattice, nacl, diamond, graphite, copper, ice, ionic, covalent, metallic, molecular, unit cell, structure",
      },
      {
        label: "Orbital Shapes",
        description: "s, p, and d orbital point clouds with phase coloring. Nodes you can actually see.",
        path: "/learn/orbitals",
        icon: Orbit,
        keywords: "orbital, s, p, d, shape, wavefunction, psi, node, hybridisation, atomic",
      },
    ],
  },
  {
    key: "calculate",
    label: "Calculate",
    tagline: "Run the math - fast, with steps shown.",
    icon: Calculator,
    tools: [
      {
        label: "Equation Balancer",
        description: "Balance any chemical equation with step-by-step coefficients.",
        path: "/tools/equation-balancer",
        icon: Scale,
        keywords: "balance, equation, stoichiometry, coefficient",
      },
      {
        label: "Molar Mass",
        description: "Compute molar mass for any formula, with element breakdown.",
        path: "/tools/molar-mass",
        icon: Calculator,
        keywords: "molar, molecular, weight, mass, formula",
      },
      {
        label: "Solubility Table",
        description: "Look up which ionic compounds dissolve in water.",
        path: "/tools/solubility",
        icon: Table,
        keywords: "solubility, dissolve, precipitate, rules",
      },
    ],
  },
  {
    key: "practice",
    label: "Practice",
    tagline: "Test yourself with custom problem sets.",
    icon: NotebookPen,
    tools: [
      {
        label: "Worksheet Generator",
        description: "Create printable worksheets across topics and difficulty.",
        path: "/worksheet",
        icon: FileText,
        keywords: "worksheet, quiz, problem, practice, printable",
      },
      {
        label: "Dimensional Analysis",
        description:
          "Drill unit conversions with the factor-label method. Lesson + randomised problems with instant grading.",
        path: "/tools/dimensional-analysis",
        icon: Ruler,
        keywords:
          "dimensional analysis, unit conversion, factor label, stoichiometry, mole, gram, liter, sig figs, drill",
      },
    ],
  },
  {
    key: "learn",
    label: "Learn",
    tagline: "Spaced-repetition flashcards for chemistry facts.",
    icon: BookOpen,
    tools: [
      {
        label: "Flashcards",
        description:
          "Element symbols, polyatomic ions, oxidation states, indicators, GHS pictograms - plus a personalised review deck of what you missed in Play.",
        path: "/learn",
        icon: BookOpen,
        keywords:
          "flashcards, learn, study, srs, spaced repetition, leitner, ions, polyatomic, oxidation, indicator, pictogram, ghs, review",
      },
      {
        label: "Electrochemistry",
        description:
          "Lesson + drill on redox, galvanic cells, EMF, and electrolysis. Standard reduction potentials reference included.",
        path: "/learn/electrochemistry",
        icon: Battery,
        keywords:
          "electrochemistry, redox, oxidation, reduction, galvanic, voltaic, electrolysis, cell, emf, anode, cathode, reduction potential, faraday",
      },
      {
        label: "Organic Primer",
        description:
          "Functional groups, IUPAC naming, isomers, and skeletal structures. Bridge to AP Chem and biology.",
        path: "/learn/organic-primer",
        icon: Hexagon,
        keywords:
          "organic, functional group, alcohol, aldehyde, ketone, ester, amine, alkane, alkene, alkyne, iupac, naming, isomer, skeletal, primer",
      },
      {
        label: "Reaction Kinetics",
        description:
          "Lesson + drill on rate factors, collision theory, rate laws, energy profiles, and catalysts.",
        path: "/learn/kinetics",
        icon: Gauge,
        keywords:
          "kinetics, rate, rate law, order, activation energy, arrhenius, catalyst, collision theory, energy profile, transition state",
      },
      {
        label: "Equilibrium & Le Chatelier",
        description:
          "Lesson + drill on dynamic equilibrium, K expressions, Q vs K, and predicting shifts from concentration, pressure, and temperature changes.",
        path: "/learn/equilibrium",
        icon: Scale,
        keywords:
          "equilibrium, le chatelier, dynamic, kc, kp, q, reaction quotient, shift, haber, exothermic, endothermic, pressure, concentration",
      },
      {
        label: "Phase-Change Lab",
        description:
          "Heating curve + live particle box + KE/PE bars. Finally explains why temperature plateaus during melting and boiling.",
        path: "/learn/phase-changes",
        icon: Snowflake,
        keywords:
          "phase, change, heating curve, melting, boiling, latent heat, fusion, vaporization, ke, pe, kinetic, potential, plateau, water, ethanol, lead",
      },
      {
        label: "Collision Simulator",
        description:
          "Watch particles bounce, react, and convert. Tune temperature, concentration, and activation energy in real time.",
        path: "/learn/kinetics/collisions",
        icon: Waves,
        keywords:
          "collision, theory, kinetics, activation energy, arrhenius, catalyst, maxwell boltzmann, orientation, rate, particles, simulation",
      },
    ],
  },
  {
    key: "play",
    label: "Play",
    tagline: "Compete head-to-head and learn faster.",
    icon: Gamepad2,
    tools: [
      {
        label: "Element of the Day",
        description:
          "A new featured element with 3 quick questions every day. Build a streak and share your result.",
        path: "/daily",
        icon: Boxes,
        keywords:
          "daily, element, trivia, streak, wordle, fact, challenge, share",
      },
      {
        label: "Element Battle",
        description: "Take turns naming elements in a category or last-letter chain.",
        path: "/play/element-battle",
        icon: Atom,
        keywords: "game, multiplayer, hot seat, elements, periodic, category, last letter",
      },
      {
        label: "Chem Duel",
        description: "HP-based trivia battle covering bonding, reactions, organic, and safety.",
        path: "/play/chem-duel",
        icon: Swords,
        keywords: "game, multiplayer, hot seat, trivia, hp, duel, bonding, reactions, safety",
      },
      {
        label: "Speed Quiz",
        description: "Race the clock to identify elements from clues. Solo or hot-seat 2-player.",
        path: "/play/speed-quiz",
        icon: Timer,
        keywords: "game, solo, multiplayer, hot seat, periodic, table, quiz, speed, timer",
      },
      {
        label: "Reaction Race",
        description: "Spot the product and reaction type before time runs out. Solo or 2-player.",
        path: "/play/reaction-race",
        icon: Flame,
        keywords: "game, solo, multiplayer, hot seat, reactions, product, equation, race",
      },
      {
        label: "Lab Safety Showdown",
        description:
          "Spot hazards, GHS pictograms, and PPE. Solo lives, hot-seat 2P, or buzzer race.",
        path: "/play/lab-safety-showdown",
        icon: ShieldAlert,
        keywords: "game, safety, lab, ghs, pictogram, ppe, hazard, buzzer, solo, multiplayer, hot seat",
      },
      {
        label: "Glassware ID",
        description:
          "Identify lab apparatus from icons before the clock runs out. Solo or hot-seat 2P.",
        path: "/play/glassware-id",
        icon: BeakerIcon,
        keywords:
          "game, solo, multiplayer, hot seat, glassware, apparatus, lab, equipment, beaker, flask, burette",
      },
      {
        label: "Acid/Base pH Sort",
        description:
          "Drop substances into the right pH band. Adjacent answers earn partial credit.",
        path: "/play/ph-sort",
        icon: Droplet,
        keywords:
          "game, solo, multiplayer, hot seat, acid, base, ph, indicator, neutral, alkaline",
      },
      {
        label: "Periodic Sprint",
        description:
          "Find the named element on the periodic table before time runs out.",
        path: "/play/periodic-sprint",
        icon: Search,
        keywords:
          "game, solo, multiplayer, hot seat, periodic, table, find, locate, sprint, element",
      },
      {
        label: "Bond Type Snap",
        description:
          "Snap-judge each compound as ionic, covalent, or metallic.",
        path: "/play/bond-type-snap",
        icon: Link2,
        keywords:
          "game, solo, multiplayer, hot seat, bond, ionic, covalent, metallic, compound",
      },
      {
        label: "Electron Config Sprint",
        description:
          "Type the ground-state electron configuration before time runs out. Solo, three difficulties.",
        path: "/play/electron-config-sprint",
        icon: Atom,
        keywords:
          "game, solo, electron, configuration, orbital, noble gas, shorthand, sprint, timed",
      },
      {
        label: "Chem-doku",
        description:
          "Sudoku with element symbols. Pick a pack, a grid size, and solve at your own pace.",
        path: "/play/chem-doku",
        icon: Grid3x3,
        keywords:
          "game, solo, sudoku, puzzle, grid, symbols, halogens, noble gas, alkali, logic",
      },
    ],
  },
  {
    key: "lab",
    label: "Lab",
    tagline: "Mix chemicals safely in a virtual lab.",
    icon: FlaskConical,
    tools: [
      {
        label: "Online Lab",
        description: "Mix reagents, run flame and pH tests, and log results.",
        path: "/lab",
        icon: Beaker,
        keywords: "lab, reaction, flame, ph, mix, beaker",
      },
    ],
  },
  {
    key: "teach",
    label: "Teach",
    tagline: "Resources for instructors and curriculum.",
    icon: GraduationCap,
    tools: [
      {
        label: "Lesson Plans",
        description: "Ready-to-use lesson plans aligned to high-school chemistry.",
        path: "/lessons",
        icon: BookOpen,
        keywords: "lesson, plan, curriculum, teacher, classroom",
      },
      {
        label: "End-of-Year Project",
        description:
          "TEKS-aligned Grade 10 capstone with 9 topics, differentiated pathways, deliverables, and a presentation rubric.",
        path: "/teach/end-of-year-project",
        icon: GraduationCap,
        keywords:
          "project, capstone, teks, grade 10, pathway, foundation, intermediate, rubric, presentation, end of year",
      },
    ],
  },
];

/** Flat list of all tools - useful for search and the /explore page. */
export const ALL_TOOLS: Array<NavTool & { categoryKey: NavCategoryKey; categoryLabel: string }> =
  NAV_CATEGORIES.flatMap((cat) =>
    cat.tools.map((tool) => ({
      ...tool,
      categoryKey: cat.key,
      categoryLabel: cat.label,
    })),
  );